import CategoryIcon from "@mui/icons-material/Category";
import TranslateIcon from "@mui/icons-material/Translate";
import {
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    MenuItem,
    Stack,
    Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import React, {
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { APP_IMAGES } from "../../assets";
import globalStyle from "../../globalStyle";
import { baseUrl } from "../../services";
import {
    getAllEcomSubCategory
} from "../../services/ecomSubCategoryApiFn";
import { createEcomSubSubCategory, updateEcomSubSubCategory } from "../../services/ecomSubSubCategoryApiFn";
import { capitalizeText, handleOpenSnackBar } from "../../utils";
import { translateText } from "../../utils/translationService";
import { addEditEcomSubSubCategoryValidation } from "../../utils/Validation";
import AppLoader from "../AppLoader";
import GlobalButton from "../CustomButton";
import CustomDropDown from "../CutomDropDown";
import KeywordsTagInput from "../KeywordsTagInput";
import Label from "../Label";
import TextInput from "../TextInputs";
import UploadBox from "../UploadBox";
import DialogHeader from "./Header";
import DialogBody from "./Body";
import DialogActionButtons from "./Footer";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(4, 2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(2),
    },
    "& .MuiPaper-root": {
        width: "900px",
        [theme.breakpoints.down("sm")]: {
            margin: 12,
        },
    },
    "& .MuiInputBase-multiline": {
        padding: "6px !important",
    },
}));

let paramsData = {};

const initialInputValues = {
    image: null,
    smallIcon: null,
    sortingNum: 0,
    categoryId: "",
    subCategoryId: "",
    name_en: "",
    name_ro: "",
    description_en: "",
    description_ro: "",
    metaTitle_en: "",
    metaTitle_ro: "",
    metaDescription_en: "",
    metaDescription_ro: "",
    metaKeywords_en: [],
    metaKeywords_ro: [],
};

const AddEditEcomSubSubCategoryDialog = forwardRef(
    ({ allEcommerCategory }, ref) => {
        const openImageFile = useRef(null);
        const openIconFile = useRef(null);
        const [open, setOpen] = useState(false);
        const { t } = useTranslation(["catalogue", "common"]);
        const [allSubCategories, setAllSubCategories] = useState([]);
        const [isLoading, setIsLoading] = useState({
            translateToEnglish: false,
            save: false,
            image: false,
            smallIcon: false,
            subCategory: false,
        });
        const [inputError, setInputError] = useState({});
        const [inputValues, setInputValues] = useState(initialInputValues);

        // Redux State
        const searchKeys = useSelector(
            (state) => state.searchKeysReducer.searchKeys,
        );
        const { currentLanguage } = useSelector((state) => state.languageReducer);

        useImperativeHandle(ref, () => ({
            openDialog(params) {
                setOpen(true);
                paramsData = params;
                if (params.type === "edit") {
                    const data = params.data;
                    fetchchedAllSubCategories(data?.categoryId);
                    setInputValues({
                        image: data?.image || null,
                        smallIcon: data?.activeIcon || null,
                        sortingNum: data?.serialNo || 0,
                        categoryId: data?.categoryId || "",
                        subCategoryId: data?.subCategoryId || "",
                        name_en: capitalizeText(data?.name?.en) || "",
                        name_ro: capitalizeText(data?.name?.ro) || "",
                        description_en: data?.description?.en || "",
                        description_ro: data?.description?.ro || "",
                        metaTitle_en: capitalizeText(data?.metaTitle?.en) || "",
                        metaTitle_ro: capitalizeText(data?.metaTitle?.ro) || "",
                        metaDescription_en: data?.metaDescription?.en || "",
                        metaDescription_ro: data?.metaDescription?.ro || "",
                        metaKeywords_en: data?.metaKeywords?.en || [],
                        metaKeywords_ro: data?.metaKeywords?.ro || [],
                    });

                }
            },

            closeDialog() {
                handleClose();
            },
        }));

        const handleClose = () => {
            setOpen(false);
            setInputValues(initialInputValues);
            setInputError({});
        };

        const handleUploadImage = async (event, type) => {
            if (!event.target.files[0]) return;

            try {
                setIsLoading((prev) => ({ ...prev, [type]: true }));
                const formData = new FormData();
                formData.append("image", event.target.files[0]);
                const response = await axios({
                    method: "post",
                    url: `${baseUrl}image/upload`,
                    data: formData,
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setInputError((pre) => ({ ...pre, [type]: false }));
                setInputValues((pre) => ({ ...pre, [type]: response?.data?.url }));
                handleOpenSnackBar("success", t("success.imageUploadSuccess", { ns: "common" }));
            } catch (error) {
                handleOpenSnackBar("error", t("error.imageUploadFailed", { ns: "common" }));
            } finally {
                openImageFile.current.value = null;
                openIconFile.current.value = null;
                setIsLoading((prev) => ({ ...prev, [type]: false }));
            }
        };

        const handleAddEditCategory = () => {
            const valid = addEditEcomSubSubCategoryValidation(
                inputValues,
                setInputError,
            );

            if (valid) {
                return handleOpenSnackBar("error", t("resolveTheIssues", { ns: "common" }));
            }

            let payload = {
                image: inputValues.image,
                activeIcon: inputValues.smallIcon,
                serialNo: inputValues.sortingNum,
                categoryId: inputValues.categoryId,
                subCategoryId: inputValues.subCategoryId,
                name: {
                    en: inputValues.name_en,
                    ro: inputValues.name_ro,
                },
                description: {
                    en: inputValues.description_en,
                    ro: inputValues.description_ro,
                },
                metaTitle: {
                    en: inputValues.metaTitle_en,
                    ro: inputValues.metaTitle_ro,
                },
                metaDescription: {
                    en: inputValues.metaDescription_en,
                    ro: inputValues.metaDescription_ro,
                },
                metaKeywords: {
                    en: inputValues.metaKeywords_en,
                    ro: inputValues.metaKeywords_ro,
                },
            };

            if (paramsData?.type === "add") {
                handleApiCall(createEcomSubSubCategory, payload, "");
            } else {
                handleApiCall(updateEcomSubSubCategory, payload, paramsData?.data?._id);
            }
        };

        const handleApiCall = async (apiFunction, payload, id) => {
            try {
                setIsLoading((prev) => ({ ...prev, save: true }));
                const response = await apiFunction(id, payload);
                if (response?.data?.status === "success") {
                    handleOpenSnackBar("success", response?.data?.message);
                    handleClose();
                    paramsData?.getData(
                        searchKeys.subCategoryName,
                        searchKeys.mainCategoryId,
                        searchKeys.subCategoryId,
                    );
                } else {
                    handleOpenSnackBar("error", response?.data?.message);
                }
            } catch (error) {
                handleOpenSnackBar("error", t("error.somethingWentWrong", { ns: "common" }));
            } finally {
                setIsLoading((prev) => ({ ...prev, save: false }));
            }
        };

        const handleTranslateToEnglish = async (key1, key2) => {
            setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
            try {
                const [englishText] = await Promise.all([
                    translateText(inputValues[key1]),
                ]);
                setInputValues((prev) => ({
                    ...prev,
                    [key2]: englishText,
                }));
                setInputError((prev) => ({
                    ...prev,
                    [key2]: "",
                }));
            } catch (error) {
                handleOpenSnackBar("error", t("error.errorWhileTranslating", { ns: "common" }));
            } finally {
                setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
            }
        };

        const handleChageInputs = (e) => {
            const { name, value, valueAsNumber, type } = e.target;

            const formatedValue = type === "number" ? valueAsNumber : value;

            if (name === "categoryId" && value) {
                fetchchedAllSubCategories(value);
                setInputValues((prev) => ({ ...prev, subCategoryId: "" }));
            }

            setInputValues((pre) => ({ ...pre, [name]: formatedValue }));
            setInputError((pre) => ({ ...pre, [name]: "" }));
        };

        const handleChangeMetaKeywords = (name, value) => {
            setInputValues((prev) => ({ ...prev, [name]: value }));
            setInputError((prev) => ({ ...prev, [name]: "" }));
        };

        const fetchchedAllSubCategories = async (value) => {
            try {
                setIsLoading((prev) => ({ ...prev, subCategory: true }));
                const response = await getAllEcomSubCategory("", value);
                if (response?.data?.status === "success") {
                    setAllSubCategories(response?.data?.data);
                } else {
                    handleOpenSnackBar(response?.data?.message, "error");
                }
            } catch (error) {
                handleOpenSnackBar(error?.message, "error");
            } finally {
                setIsLoading((prev) => ({ ...prev, subCategory: false }));
            }
        };

        return (
            <>
                <AppLoader isVisible={isLoading.save} />
                <BootstrapDialog
                    onClose={handleClose}
                    aria-labelledby="customized-dialog-title"
                    open={open}
                >
                    <DialogHeader
                        title={paramsData.type === "add"
                            ? t("addSubSubCategory")
                            : t("editSubSubCategory")}
                        secondaryHeading={paramsData.type === "add"
                            ? t("addSubSubCategoryInstruction")
                            : t("editSubSubCategoryInstruction")}
                        onClose={handleClose}
                        icon={<CategoryIcon />}
                    />

                    <DialogBody>
                        <Grid container spacing={2}>
                            {/* Large Image */}
                            <Grid item xs={12}>
                                <UploadBox
                                    inputRef={openImageFile}
                                    label={t("image", { ns: "common" })}
                                    note={t("largeImageSizeNote", { ns: "common" })}
                                    value={inputValues.image}
                                    error={inputError.image}
                                    loading={isLoading.image}
                                    placeholderImage={APP_IMAGES.uploadImage}
                                    accept="image/png, image/jpeg"
                                    height={{ xs: 200, md: 130 }}
                                    previewSize={{ xs: 160, md: 180 }}
                                    onClick={() => openImageFile.current.click()}
                                    onChange={(e) => handleUploadImage(e, "image")}
                                    onRemove={() =>
                                        setInputValues({ ...inputValues, image: null })
                                    }
                                />
                            </Grid>
                            {/* Small Icon */}
                            <Grid item xs={12}>
                                <UploadBox
                                    inputRef={openIconFile}
                                    label={t("smallIcon", { ns: "common" })}
                                    note={t("iconDimensionNote", { ns: "common" })}
                                    value={inputValues.smallIcon}
                                    error={inputError.smallIcon}
                                    loading={isLoading.smallIcon}
                                    placeholderImage={APP_IMAGES.uploadImage}
                                    accept="image/png, image/jpeg"
                                    height={{ xs: 200, md: 130 }}
                                    previewSize={{ xs: 100, md: 100 }}
                                    onClick={() => openIconFile.current.click()}
                                    onChange={(e) => handleUploadImage(e, "smallIcon")}
                                    onRemove={() =>
                                        setInputValues({ ...inputValues, smallIcon: null })
                                    }
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Label
                                    required
                                    labelText={t("selectCategory")}
                                    htmlFor={"sub-category-categoryId"}
                                />
                                <CustomDropDown
                                    id={"sub-category-categoryId"}
                                    name={"categoryId"}
                                    value={inputValues.categoryId}
                                    onChange={handleChageInputs}
                                    inputError={Boolean(inputError.categoryId)}
                                    errorText={t(inputError.categoryId)}
                                    sx={{
                                        "& .MuiSelect-outlined": {
                                            textTransform: "capitalize",
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        {t("selectCategory")}
                                    </MenuItem>
                                    {allEcommerCategory.map((item, index) => (
                                        <MenuItem
                                            key={index}
                                            value={item?._id}
                                            sx={{ textTransform: "capitalize" }}
                                        >
                                            {item.name[currentLanguage]}
                                        </MenuItem>
                                    ))}
                                </CustomDropDown>
                            </Grid>

                            <Grid item xs={12}>
                                <Label
                                    required
                                    labelText={t("selectSubCategory")}
                                    htmlFor={"sub-category-subCategoryId"}
                                />
                                <CustomDropDown
                                    id={"sub-category-subCategoryId"}
                                    name={"subCategoryId"}
                                    value={inputValues.subCategoryId}
                                    onChange={handleChageInputs}
                                    inputError={Boolean(inputError.subCategoryId)}
                                    errorText={t(inputError.subCategoryId)}
                                    sx={{
                                        "& .MuiSelect-outlined": {
                                            textTransform: "capitalize",
                                        },
                                    }}

                                    icon={
                                        isLoading.subCategory && (
                                            <CircularProgress size={18} color="inherit" />
                                        )
                                    }
                                    disabled={isLoading.subCategory}
                                >
                                    <MenuItem value="" disabled>
                                        {t("selectSubCategory")}
                                    </MenuItem>
                                    {allSubCategories.map((item, index) => (
                                        <MenuItem
                                            key={index}
                                            value={item?._id}
                                            sx={{ textTransform: "capitalize" }}
                                        >
                                            {item?.name?.[currentLanguage]}
                                        </MenuItem>
                                    ))}
                                </CustomDropDown>
                            </Grid>

                            <Grid item xs={12}>
                                <Label
                                    required
                                    labelText={t("order")}
                                    htmlFor={"sub-category-sorting-number"}
                                />
                                <TextInput
                                    id={"sub-category-sorting-number"}
                                    styleProps={globalStyle.textField}
                                    placeholder={t("enterOrder")}
                                    required
                                    name={"sortingNum"}
                                    type={"number"}
                                    value={inputValues.sortingNum}
                                    onChange={handleChageInputs}
                                    inputError={inputError.sortingNum}
                                    helperText={t(inputError.sortingNum)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Label
                                    required
                                    labelText={t("categoryNameInRomanian")}
                                    htmlFor={`catrgory-name-ro`}
                                />
                                <TextInput
                                    id={`catrgory-name-ro`}
                                    required
                                    placeholder={t("enterNameInRomanian")}
                                    name={`name_ro`}
                                    textLength={60}
                                    value={inputValues.name_ro}
                                    onChange={handleChageInputs}
                                    inputError={Boolean(inputError.name_ro)}
                                    helperText={t(inputError.name_ro)}
                                    InputEndIcon={
                                        <Typography variant="body2" color="text.grey">
                                            {inputValues.name_ro?.length}/60
                                        </Typography>
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack
                                    mb={"5px"}
                                    direction={"row"}
                                    alignItems={"center"}
                                    justifyContent={"space-between"}
                                >
                                    <Label
                                        required
                                        labelText={t("categoryNameInEnglish")}
                                        htmlFor={`catrgory-name-en`}
                                        labelProps={{ marginBottom: 0 }}
                                    />
                                    <Chip
                                        label={t("translateToEnglish", { ns: "common" })}
                                        size="small"
                                        onClick={() =>
                                            handleTranslateToEnglish("name_ro", "name_en")
                                        }
                                        icon={
                                            isLoading.translateToEnglish ? (
                                                <CircularProgress size={16} color="inherit" />
                                            ) : (
                                                <TranslateIcon />
                                            )
                                        }
                                        disabled={isLoading.translateToEnglish}
                                    />
                                </Stack>
                                <TextInput
                                    id={`catrgory-name-en`}
                                    required
                                    placeholder={t("enterNameInEnglish")}
                                    name={`name_en`}
                                    textLength={60}
                                    value={inputValues.name_en}
                                    onChange={handleChageInputs}
                                    inputError={Boolean(inputError.name_en)}
                                    helperText={t(inputError.name_en)}
                                    InputEndIcon={
                                        <Typography variant="body2" color="text.grey">
                                            {inputValues.name_en?.length}/60
                                        </Typography>
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Label
                                    required
                                    labelText={t("categoryDescriptionInRomanian")}
                                    htmlFor={`catrgory-description-ro`}
                                />
                                <TextInput
                                    id={`catrgory-description-ro`}
                                    required
                                    placeholder={t("enterDescriptionInRomanian")}
                                    name={`description_ro`}
                                    textLength={255}
                                    value={inputValues.description_ro}
                                    onChange={handleChageInputs}
                                    inputError={Boolean(inputError.description_ro)}
                                    helperText={t(inputError.description_ro)}
                                    multiline
                                    rows={3}
                                    InputEndIcon={
                                        <Typography variant="body2" color="text.grey">
                                            {inputValues.description_ro?.length}/255
                                        </Typography>
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack
                                    mb={"5px"}
                                    direction={"row"}
                                    alignItems={"center"}
                                    justifyContent={"space-between"}
                                >
                                    <Label
                                        required
                                        labelText={t("categoryDescriptionInEnglish")}
                                        htmlFor={`catrgory-description-en`}
                                        labelProps={{ marginBottom: 0 }}
                                    />
                                    <Chip
                                        label={t("translateToEnglish", { ns: "common" })}
                                        size="small"
                                        onClick={() =>
                                            handleTranslateToEnglish(
                                                "description_ro",
                                                "description_en",
                                            )
                                        }
                                        icon={
                                            isLoading.translateToEnglish ? (
                                                <CircularProgress size={16} color="inherit" />
                                            ) : (
                                                <TranslateIcon />
                                            )
                                        }
                                        disabled={isLoading.translateToEnglish}
                                    />
                                </Stack>
                                <TextInput
                                    id={`catrgory-description-en`}
                                    required
                                    placeholder={t("enterDescriptionInEnglish")}
                                    name={`description_en`}
                                    textLength={255}
                                    value={inputValues.description_en}
                                    onChange={handleChageInputs}
                                    inputError={Boolean(inputError.description_en)}
                                    helperText={t(inputError.description_en)}
                                    multiline
                                    rows={3}
                                    InputEndIcon={
                                        <Typography variant="body2" color="text.grey">
                                            {inputValues.description_en?.length}/255
                                        </Typography>
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="h6" fontWeight={600}>
                                    {t("categoryMetaInfo")}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Label
                                    required
                                    labelText={t("categoryMetaTitleInRomanian")}
                                    htmlFor={`catrgory-meta-title-ro`}
                                />
                                <TextInput
                                    id={`catrgory-meta-title-ro`}
                                    required
                                    placeholder={t("enterMetaTitleInRomanian")}
                                    name={`metaTitle_ro`}
                                    textLength={255}
                                    value={inputValues.metaTitle_ro}
                                    onChange={handleChageInputs}
                                    inputError={Boolean(inputError.metaTitle_ro)}
                                    helperText={t(inputError.metaTitle_ro)}
                                    InputEndIcon={
                                        <Typography variant="body2" color="text.grey">
                                            {inputValues.metaTitle_ro?.length}/255
                                        </Typography>
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack
                                    mb={"5px"}
                                    direction={"row"}
                                    alignItems={"center"}
                                    justifyContent={"space-between"}
                                >
                                    <Label
                                        required
                                        labelText={t("categoryMetaTitleInEnglish")}
                                        htmlFor={`catrgory-meta-title-en`}
                                        labelProps={{ marginBottom: 0 }}
                                    />
                                    <Chip
                                        label={t("translateToEnglish", { ns: "common" })}
                                        size="small"
                                        onClick={() =>
                                            handleTranslateToEnglish("metaTitle_ro", "metaTitle_en")
                                        }
                                        icon={
                                            isLoading.translateToEnglish ? (
                                                <CircularProgress size={16} color="inherit" />
                                            ) : (
                                                <TranslateIcon />
                                            )
                                        }
                                        disabled={isLoading.translateToEnglish}
                                    />
                                </Stack>
                                <TextInput
                                    id={`catrgory-meta-title-en`}
                                    required
                                    placeholder={t("enterMetaTitleInEnglish")}
                                    name={`metaTitle_en`}
                                    textLength={255}
                                    value={inputValues.metaTitle_en}
                                    onChange={handleChageInputs}
                                    inputError={Boolean(inputError.metaTitle_en)}
                                    helperText={t(inputError.metaTitle_en)}
                                    InputEndIcon={
                                        <Typography variant="body2" color="text.grey">
                                            {inputValues.metaTitle_en?.length}/255
                                        </Typography>
                                    }
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Label
                                    required
                                    labelText={t("categoryMetaDescriptionInRomanian")}
                                    htmlFor={`catrgory-meta-description-ro`}
                                />
                                <TextInput
                                    id={`catrgory-meta-description-ro`}
                                    required
                                    placeholder={t("enterMetaDescriptionInRomanian")}
                                    name={`metaDescription_ro`}
                                    textLength={255}
                                    value={inputValues.metaDescription_ro}
                                    onChange={handleChageInputs}
                                    inputError={Boolean(inputError.metaDescription_ro)}
                                    helperText={t(inputError.metaDescription_ro)}
                                    multiline
                                    rows={3}
                                    InputEndIcon={
                                        <Typography variant="body2" color="text.grey">
                                            {inputValues.metaDescription_ro?.length}/255
                                        </Typography>
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack
                                    mb={"5px"}
                                    direction={"row"}
                                    alignItems={"center"}
                                    justifyContent={"space-between"}
                                >
                                    <Label
                                        required
                                        labelText={t("categoryMetaDescriptionInEnglish")}
                                        htmlFor={`catrgory-meta-description-en`}
                                        labelProps={{ marginBottom: 0 }}
                                    />
                                    <Chip
                                        label={t("translateToEnglish", { ns: "common" })}
                                        size="small"
                                        onClick={() =>
                                            handleTranslateToEnglish(
                                                "metaDescription_ro",
                                                "metaDescription_en",
                                            )
                                        }
                                        icon={
                                            isLoading.translateToEnglish ? (
                                                <CircularProgress size={16} color="inherit" />
                                            ) : (
                                                <TranslateIcon />
                                            )
                                        }
                                        disabled={isLoading.translateToEnglish}
                                    />
                                </Stack>
                                <TextInput
                                    id={`catrgory-meta-description-en`}
                                    required
                                    placeholder={t("enterMetaDescriptionInEnglish")}
                                    name={`metaDescription_en`}
                                    textLength={255}
                                    value={inputValues.metaDescription_en}
                                    onChange={handleChageInputs}
                                    inputError={Boolean(inputError.metaDescription_en)}
                                    helperText={t(inputError.metaDescription_en)}
                                    multiline
                                    rows={3}
                                    InputEndIcon={
                                        <Typography variant="body2" color="text.grey">
                                            {inputValues.metaDescription_en?.length}/255
                                        </Typography>
                                    }
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Label
                                    required
                                    labelText={t("metaKeywordsInRomanian")}
                                    htmlFor={`catrgory-meta-keywords-ro`}
                                />
                                <KeywordsTagInput
                                    placeholder={t("enterMetaKeywordsInRomanian")}
                                    value={inputValues.metaKeywords_ro}
                                    onChange={(newValue) =>
                                        handleChangeMetaKeywords("metaKeywords_ro", newValue)
                                    }
                                    textFieldProps={{
                                        error: Boolean(inputError.metaKeywords_ro),
                                        helperText: t(inputError.metaKeywords_ro),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Label
                                    required
                                    labelText={t("metaKeywordsInEnglish")}
                                    htmlFor={`catrgory-meta-keywords-en`}
                                />
                                <KeywordsTagInput
                                    placeholder={t("enterMetaKeywordsInEnglish")}
                                    value={inputValues.metaKeywords_en}
                                    onChange={(newValue) =>
                                        handleChangeMetaKeywords("metaKeywords_en", newValue)
                                    }
                                    textFieldProps={{
                                        error: Boolean(inputError.metaKeywords_en),
                                        helperText: t(inputError.metaKeywords_en),
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </DialogBody>

                    <DialogActionButtons
                        onConfirm={handleAddEditCategory}
                        onCancel={handleClose}
                        confirmText={t("save")}
                        cancelText={t("cancel")}
                        isConfirmBtnDisable={isLoading.save}
                        showCancelButton={true}
                    />
                </BootstrapDialog>
            </>
        );
    },
);

export default AddEditEcomSubSubCategoryDialog;
